import type { ChaseRecommendation } from '@/api/client'

const REVIEW_LABEL: Record<ChaseRecommendation['review'], string> = {
  standard: 'Leave your review',
  skip: 'Skip - no review, no message',
}

const THANK_YOU_LABEL: Record<ChaseRecommendation['thank_you'], string> = {
  standard: 'Standard thank-you (say you left five stars)',
  soft: 'Softened thank-you - acknowledge the stay, mention your review, no five-star ask',
  none: 'No thank-you',
}

const D7_LABEL: Record<ChaseRecommendation['reminder_d7'], string> = {
  optional: 'One reminder, if the guest is still silent',
  one_soft: 'At most one, softly worded, if the guest is still silent',
  never: 'None',
}

/**
 * Whether a step is something to do, something deliberately not done, or a
 * moment that does not exist for this reservation at all (a VRBO stay has no
 * day-7 deadline; a red-tier stay has no thank-you).
 */
export type StepState = 'do' | 'skip' | 'na'

export interface Step {
  key: string
  name: string
  when: string
  action: string
  state: StepState
}

/**
 * Turns the recommendation into the four moments, in the order they happen.
 */
export function buildChaseSteps(recommendation: ChaseRecommendation): Step[] {
  const reviewing = recommendation.review === 'standard'
  const thanking = recommendation.thank_you !== 'none'
  const reminding =
    recommendation.reminder_d7 === 'optional' || recommendation.reminder_d7 === 'one_soft'

  return [
    {
      key: 'review',
      name: 'Your review',
      when: 'Now',
      action: REVIEW_LABEL[recommendation.review],
      state: reviewing ? 'do' : 'skip',
    },
    {
      key: 'thank_you',
      name: 'Thank-you',
      when: 'Now',
      action: THANK_YOU_LABEL[recommendation.thank_you],
      state: thanking ? 'do' : 'skip',
    },
    {
      key: 'd7',
      name: 'Reminder',
      when: 'Day 7',
      action: D7_LABEL[recommendation.reminder_d7],
      state: recommendation.reminder_d7 === 'never' ? 'na' : reminding ? 'do' : 'skip',
    },
    {
      key: 'd13',
      name: 'Second reminder',
      when: 'Day 13',
      action: 'Never - has not produced a review in the study',
      state: 'na',
    },
  ]
}
